import React from "react";
import { cn } from "@/lib/utils";

interface Tab<T extends string = string> {
  id: T;
  label: string;
  disabled?: boolean;
}

interface TabGroupProps<T extends string = string> {
  tabs: Tab<T>[];
  activeTab: T;
  onChange: (tab: T) => void;
  variant?: 'default' | 'rounded' | 'underline';
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

function TabGroup<T extends string = string>({
  tabs,
  activeTab,
  onChange,
  variant = 'default',
  size = 'md',
  className
}: TabGroupProps<T>) {
  const containerVariants = {
    default: "flex items-center rounded-t-lg bg-gray-light shadow-[0_4px_16px_0_rgba(0,0,0,0.16)]",
    rounded: "flex h-10 p-0.5 items-center rounded-[32px] border border-black/12 bg-background",
    underline: "flex items-center border-b border-border bg-background"
  };

  const tabVariants = {
    default: {
      base: "flex flex-col justify-end items-center gap-2 h-full px-4 cursor-pointer transition-colors",
      active: "bg-white rounded-tl-lg",
      inactive: "hover:bg-gray-200",
      lastInactive: "rounded-tr-lg"
    },
    rounded: {
      base: "flex items-center justify-center px-4 py-2 text-sm font-medium rounded-[28px] cursor-pointer transition-all",
      active: "bg-[#E6EEFB] border border-gray-medium text-gray-text",
      inactive: "text-gray-medium hover:text-gray-text hover:bg-gray-50"
    },
    underline: {
      base: "flex items-center justify-center px-4 py-2 text-sm font-medium cursor-pointer transition-all relative",
      active: "text-gray-text",
      inactive: "text-gray-medium hover:text-gray-text"
    }
  };

  const sizes = {
    sm: "text-sm h-12",
    md: "text-sm h-14",
    lg: "text-base h-16"
  };

  const textSizes = {
    sm: "text-sm font-bold",
    md: "text-sm font-bold",
    lg: "text-base font-bold"
  };

  return (
    <div className={cn(containerVariants[variant], sizes[size], className)}>
      {tabs.map((tab, index) => {
        const isActive = activeTab === tab.id;
        const isLast = index === tabs.length - 1;

        return (
          <div
            key={tab.id}
            className={cn(
              tabVariants[variant].base,
              isActive
                ? tabVariants[variant].active
                : tabVariants[variant].inactive,
              variant === 'default' && !isActive && isLast && tabVariants.default.lastInactive,
              tab.disabled && "opacity-50 cursor-not-allowed",
              variant === 'rounded' && index > 0 && "ml-2"
            )}
            onClick={() => !tab.disabled && onChange(tab.id)}
            role="tab"
            aria-selected={isActive}
            aria-disabled={tab.disabled}
          >
            <span className={cn(
              textSizes[size],
              isActive ? "text-gray-text" : "text-gray-medium"
            )}>
              {tab.label}
            </span>

            {variant === 'default' && (
              <div
                className={cn(
                  "h-0.5 w-full transition-colors",
                  isActive ? "bg-yellow-primary" : "bg-gray-light"
                )}
              />
            )}

            {variant === 'underline' && isActive && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gray-text" />
            )}
          </div>
        );
      })}
    </div>
  );
}

export { TabGroup, type TabGroupProps, type Tab };
export default TabGroup;